import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';

import {
  channelsSelector,
  collectionCurrentPageSelector,
  collectionSelector,
  collectionTitleSelector,
  getCurrentPageNumberSelector,
  getRelatedVideosFeatureSelector,
  getVideosPerPageSelector,
  websiteSelector,
} from 'common/selectors';
import {
  ChannelOverview,
  EmptyChannelView,
  urlChannelIdSelector,
  urlChannelSlugSelector,
  urlVideoIdSelector,
  VideoControlsMenu,
} from 'pages/channel.page';
import { urlSortSelector } from 'common/selectors/router';
import { integrationSelector } from 'common/selectors/integration';
import { channelOverviewSelector } from 'common/selectors/settings';
import { errorHandleAction } from 'components/error-handle/actions';
import { collectionRequestAction, collectionRequestOnMobileAction, collectionResetAction } from 'common/actions';
import { PageDecorated } from 'pages/page';
import { isIframe } from 'integration/integration.utils';
import { VideoBoxDecorated } from 'components/video-box';
import { videoBoxRequestAction } from 'components/video-box/actions';
import { Header } from 'components/header';
import { ContentBox } from 'components/content-box';
import { VideoCollection } from 'components/video-collection';
import { VideoListDecorated } from 'components/video-list';
import { FeaturedMask } from 'components/video-item/masks';
import { SORTING_KEYS } from 'utils/enums';
import { Breadcrumbs } from 'pages/channel.page/breadcrumbs';
import { VideoContextMenuDecorated } from 'components/video-context-menu';
import { history } from 'app.history';

export const ChannelPageDecorated = ({ match }) => {
  const dispatch = useDispatch();

  const collectionTitle = useSelector(collectionTitleSelector);
  const collection = useSelector(collectionSelector);
  const collectionCurrentPage = useSelector(collectionCurrentPageSelector);
  const currentChannelId = useSelector((state) => urlChannelIdSelector(match.params)(state));
  const currentChannelSlug = useSelector((state) => urlChannelSlugSelector(state, match.params));
  const pageNumber = useSelector((state) => getCurrentPageNumberSelector(state, match));
  const videoId = useSelector((state) => urlVideoIdSelector(state, match));
  const sort = useSelector((state) => urlSortSelector(state, match));
  const website = useSelector(websiteSelector);
  const withRelatedVideos = useSelector(getRelatedVideosFeatureSelector);
  const integration = useSelector(integrationSelector);
  const channelOverview = useSelector(channelOverviewSelector);
  const channels = useSelector(channelsSelector);
  const videosPerPage = useSelector(getVideosPerPageSelector);

  const videos = collection.videos;

  const [isEmptyChannel, setIsEmptyChannel] = useState(false);
  const [baseUrl, setBaseUrl] = useState(`/channel/${currentChannelId}/${currentChannelSlug}`);

  useEffect(() => {
    setBaseUrl(`/channel/${currentChannelId}/${currentChannelSlug}`);
    setIsEmptyChannel(false);
  }, [currentChannelId, currentChannelSlug]);

  useEffect(() => {
    if (videos && !videoId) {
      if (currentChannelId === collection.channelId) {
        const channel = channels.find((c) => c.id === currentChannelId);
        const baseLink = `/channel/${currentChannelId}/${currentChannelSlug}/1`;

        if (channel?.startVideoId) {
          history.replace(`${baseLink}/${channel.startVideoId}`);
          return;
        }

        if (videos.length === 0) {
          setIsEmptyChannel(true);
        } else {
          history.replace(`${baseLink}/${videos[0].id}`);
        }
      }
    }
  }, [videos, collection, videoId, currentChannelId, currentChannelSlug, channels]);

  useEffect(() => {
    return () => {
      dispatch(collectionResetAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!currentChannelId) {
      dispatch(
        errorHandleAction(`You don't have the permission to see the page. Please contact your administrator`, 401)
      );
    }
  }, [currentChannelId, dispatch]);

  return (
    <PageDecorated hasVideoListArea="true" className="ct-channel-page">
      <EmptyChannelView isIframe={isIframe(integration)} channelName={collectionTitle} isEmpty={isEmptyChannel} />

      {!isEmptyChannel && currentChannelId && (
        <>
          <VideoBoxDecorated
            playerConfiguration={website.playerConfiguration}
            videoBoxRequestAction={videoBoxRequestAction(videoId)}
            withRelatedVideos={withRelatedVideos}
            inlineVideoControls={VideoControlsMenu}
          />
          <div>
            {isIframe(integration) && <Header />}
            <ContentBox id="page-video-lists" className={`animate-height`}>
              <Breadcrumbs />
              <VideoCollection
                baseUrl={baseUrl}
                sortBy={[SORTING_KEYS.display_date, SORTING_KEYS.title_asc, SORTING_KEYS.plays, SORTING_KEYS.rating]}
                withSort={true}
                loadCollectionAction={collectionRequestAction(currentChannelId, pageNumber, videosPerPage, sort, true)}
                loadMoreAction={collectionRequestOnMobileAction(
                  currentChannelId,
                  collectionCurrentPage,
                  videosPerPage,
                  sort,
                  collection.videos,
                  true
                )}
              >
                <VideoListDecorated baseUrl={baseUrl} maskJSX={FeaturedMask} contextMenu={VideoContextMenuDecorated} />
              </VideoCollection>
            </ContentBox>
          </div>
          <ChannelOverview
            withRelatedVideos={withRelatedVideos}
            containerWide={withRelatedVideos}
            overview={channelOverview}
            channels={channels}
            currentChannelId={currentChannelId}
          />
        </>
      )}
    </PageDecorated>
  );
};

export * from './selectors';
export * from './fragments/empty-channel.view';
export * from './fragments/video-controls-menu';
export * from './channel-overview';
